import React, { useCallback, useEffect, useState } from 'react';
import { TextFormat, Translate, getSortState, translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';

import { ArrowDownwardRounded, OpenInNewRounded } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  ThemeProvider,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { APP_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { addDays, addMonths, addYears, differenceInDays, differenceInMonths, differenceInYears } from 'date-fns';
import { getEntities } from './device-communication.reducer';

export const DeviceCommunication = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(
      {
        ...getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'daysSinceLastCommunication'),
        order: DESC,
      },
      props.location.search
    )
  );
  const [companySearch, setCompanySearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');

  const deviceCommunicationList = useAppSelector(state => state.deviceCommunication.entities);
  const loading = useAppSelector(state => state.deviceCommunication.loading);
  const totalItems = useAppSelector(state => state.deviceCommunication.totalItems);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const getAllEntities = useCallback(() => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        companySearch,
        assetSearch,
      })
    );
  }, [paginationState]);

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = (_, page) =>
    setPaginationState({
      ...paginationState,
      activePage: page,
    });

  const { match } = props;

  const headers: IHeaderItem[] = [
    { key: 'companyIdentification', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.companyIdentification" /> },
    { key: 'companyName', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.companyName" /> },
    { key: 'representativeName', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.representativeName" /> },
    { key: 'chassisNumber', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.assetChassis" /> },
    { key: 'assetLicensePlate', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.assetLicensePlate" /> },
    { key: 'firstCommunicationDate', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.firstActivationDate" /> },
    { key: 'lastCommunicationDate', sortable: true, component: <Translate contentKey="fleetSenseApp.deviceCommunication.lastCommunicationDate" /> },
    { key: 'lastUserLogin', sortable: false, component: <Translate contentKey="fleetSenseApp.deviceCommunication.lastLoginUser" /> },
    { key: 'assetSerial', sortable: false, component: <Translate contentKey="fleetSenseApp.deviceCommunication.assetSerial" /> },
    { key: 'iccid', sortable: false, component: <Translate contentKey="fleetSenseApp.deviceCommunication.iccid" /> },
  ];

  const formatDetailedDistance = days => {
    const today = new Date();
    const pastDate = addDays(today, -days);

    const years = differenceInYears(today, pastDate);
    const months = differenceInMonths(today, addYears(pastDate, years));
    const remainingDays = differenceInDays(today, addMonths(addYears(pastDate, years), months));

    const yearStr = years > 0 ? `${years} ano${years > 1 ? 's' : ''}` : '';
    const monthStr = months > 0 ? `${months} ${months > 1 ? 'meses' : 'mês'}` : '';
    const dayStr = remainingDays > 0 ? `${remainingDays} dia${remainingDays > 1 ? 's' : ''}` : '';

    const result = [yearStr, monthStr, dayStr].filter(Boolean).join(' e ');

    return result || 'Hoje';
  };

  const renderChip = (days: number) => {
    const label = days === null ? 'Sem comunicação' : formatDetailedDistance(days);
    let color;

    if (days === null) {
      color = 'error';
    } else if (days < 8) {
      color = 'success';
    } else {
      color = 'warning';
    }

    return <Chip label={label} color={color} sx={{ width: '140px' }} />;
  };

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.deviceCommunication.companyName'),
      value: companySearch,
      placeholder: translate('userManagement.companySearch'),
      setValue: setCompanySearch,
    },
    {
      label: translate('fleetSenseApp.deviceCommunication.assetLicensePlate'),
      value: assetSearch,
      placeholder: translate('fleetSenseApp.deviceCommunication.assetSearch'),
      setValue: setAssetSearch,
    },
  ];

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ backgroundColor: 'primary.main', borderRadius: '16px', padding: '24px' }}>
        <PageHeader
          headingId="fleetSenseApp.deviceCommunication.home.title"
          headingDataCy="deviceCommunicationHeading"
          contentKey="fleetSenseApp.deviceCommunication.home.title"
          buttonTextKey="fleetSenseApp.deviceCommunication.home.createLabel"
          match={match}
          showButton={false}
        />
        <PageFilters searchTypes={searchTypes} setActivePage={setActivePage} showFilters={['filters']} />

        {!loading && !!deviceCommunicationList.length && (
          <TableContainer sx={{ marginTop: '25px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((header, i) => (
                    <TableCell
                      key={`entity-${i}`}
                      sx={{
                        borderBottom: 'none',
                        paddingBottom: 1,
                      }}
                    >
                      {header.sortable ? (
                        <TableSortLabel
                          active={paginationState.sort === header.key}
                          direction={paginationState.order as 'desc' | 'asc'}
                          onClick={sort(header.key)}
                          IconComponent={ArrowDownwardRounded}
                        >
                          <Typography
                            sx={{
                              color: 'text.primary',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {header.component}
                          </Typography>
                        </TableSortLabel>
                      ) : (
                        <Typography
                          sx={{
                            color: 'text.primary',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {header.component}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {deviceCommunicationList?.map((device, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <CustomBodyTableCell>{device.companyIdentification ? device.companyIdentification : '-'}</CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.companyName}
                      <IconButton color="secondary" component={Link} to={`company?nameSearch=${device.companyName}`}>
                        <OpenInNewRounded fontSize="small" />
                      </IconButton>
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>{device.representativeName ? device.representativeName : '-'}</CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.chassisNumber ? device.chassisNumber : '-'}
                      <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${device.assetId}`}>
                        <OpenInNewRounded fontSize="small" />
                      </IconButton>
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.assetLicensePlate}
                      <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${device.assetId}`}>
                        <OpenInNewRounded fontSize="small" />
                      </IconButton>
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.firstCommunicationDate ? <TextFormat type="date" value={device.firstCommunicationDate} format={APP_DATE_FORMAT} /> : '-'}
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.lastCommunicationDate ? <TextFormat type="date" value={device.lastCommunicationDate} format={APP_DATE_FORMAT} /> : '-'}
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.lastLoginUser ? <TextFormat type="date" value={device.lastLoginUser} format={APP_DATE_FORMAT} /> : '-'}
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>
                      {device.assetSerial ? device.assetSerial : '-'}
                      {isAdmin && (
                        <IconButton color="secondary" component={Link} to={`dashboards/truck-implement/${device.assetId}/devices-sensors`}>
                          <OpenInNewRounded fontSize="small" />
                        </IconButton>
                      )}
                    </CustomBodyTableCell>
                    <CustomBodyTableCell>{device.iccid ? device.iccid : '-'}</CustomBodyTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && !deviceCommunicationList.length && <NoData title="Nenhum registro encontrado" />}
        {loading && <Loading />}
        <PaginationComponent paginationState={paginationState} totalItems={totalItems} onChange={handlePagination} list={deviceCommunicationList} />
      </Box>
    </ThemeProvider>
  );
};

export default DeviceCommunication;

const CustomBodyTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: '8px solid',
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  whiteSpace: 'nowrap',
}));
